import { ComplaintActivityTables } from './ComplaintActivityTables';
import BackSection from '../../Services/BackSection';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import styles from './ComplaintDetail.module.css';
import ComplaintDetailSkeleton from './ComplaintDetailSkeleton';
import { getCompliantDetails } from '../ComplaintsApi';
import Error from '../Error';

// ── Helpers ────────────────────────────────────────────────────────────────

/** Fields to exclude from the auto-mapped "Complaint Details" card */
const EXCLUDED_FIELDS = new Set([
  'PIRM_Base__Complaint_Narrative__c',
  'PIRM_Base__Resolution_Summary__c',
  'PIRM_Base__Response_To_Customer__c',
]);

/** Render a single {label, value} field */
const DataField = ({ fieldData }) => {
  if (!fieldData) return null;
  const val = fieldData.value;
  return (
    <div className={styles.field}>
      <label>{fieldData.label}</label>
      <div className={styles.value}>
        {typeof val === 'boolean' ? (val ? 'Yes' : 'No') : val || '—'}
      </div>
    </div>
  );
};

/** Render a narrative / text-area style field */
const NarrativeCard = ({ fieldData, fullWidth = true }) => {
  if (!fieldData) return null;
  return (
    <div className={`${styles.detailCard} ${fullWidth ? styles.detailFull : ''}`}>
      <h3>{fieldData.label}</h3>
      <div className={styles.narrativeBox}>
        {fieldData.value || '—'}
      </div>
    </div>
  );
};

// ── Component ──────────────────────────────────────────────────────────────

export default function ComplaintDetail() {
  const { id } = useParams();


  const { data, isLoading, error } = useQuery({
    queryKey: ['complaint', id],
    queryFn: () => getCompliantDetails(id),
  });

  if (isLoading) return <ComplaintDetailSkeleton />;
  if (error || !data?.details?.[0]) {
    const errorMsg = error?.message || 'Please contact your PIRM administrator if there is an issue.';
    return <Error errorMsg={errorMsg} />; // Removed the extra ';'
  }


  const complaint = data.details[0];
  const { tasks, events } = data;

  const mainFields = Object.entries(complaint).filter(
    ([key]) => !EXCLUDED_FIELDS.has(key)
  );

  return (
    <motion.div
      className={styles.page}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      {/* Back */}
      <BackSection pageName="Complaints" to="/complaints" />

      {/* Header */}
      <div className={`${styles.detailHeader} mt-3`}>
        <h1>
          {complaint.Name?.value}
          {complaint['PIRM_Base__Company__c']?.value && (
            <span> — {complaint['PIRM_Base__Company__c'].value}</span>
          )}
        </h1>
      </div>

      <div className={styles.detailGrid}>

        {/* ── Dynamic "Complaint Details" card ── */}
        <div className={`${styles.detailCard} ${styles.detailFull}`}>
          <h3>Complaint Details</h3>
          <div className={styles.fieldsGrid}>
            {mainFields.map(([key, fieldData]) => (
              <DataField key={key} fieldData={fieldData} />
            ))}
          </div>
        </div>

        {/* ── Narrative ── */}
        <NarrativeCard fieldData={complaint.PIRM_Base__Complaint_Narrative__c} />

        {/* ── Tasks & Events tables ── */}
        <ComplaintActivityTables tasks={tasks} events={events} />

        {/* ── Response to Customer ── */}
        <NarrativeCard fieldData={complaint.PIRM_Base__Response_To_Customer__c} />

        {/* ── Resolution Summary ── */}
        <NarrativeCard fieldData={complaint.PIRM_Base__Resolution_Summary__c} />

      </div>
    </motion.div>
  );
}